import { Button, MenuItem, Typography } from '@mui/material';
import type { CustomerSettings } from '@weft/types';
import { OVERGAE_SETTINGS,
  PAYMENT_PROVIDERS,
  UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT,
  type UpdateCustomer } from '@weft/types';
import type { Optional } from '@weft/utils';
import { isNotEmptyString, values } from '@weft/utils';
import { CurrenciesSelect } from 'apps/app/src/components/design/selects/CurrenciesSelect';
import { InvoiceDueDateSelect } from 'apps/app/src/components/design/selects/InvoiceDueDateSelect';
import { PaymentProviderSelect } from 'apps/app/src/components/design/selects/PaymentProviderSelect';
import { StyledTextField } from 'apps/app/src/components/design/StyledTextField';
import { vyFormRegister } from 'apps/app/src/utils/react-hook-forms-utils';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '../../../../components/design/Input';
import { getOverageSettingDescription } from '../../CreateCustomerPage/create-customer-page-components/CreateCustomerSettings';
import { StripeCustomerDialogForm } from '../../Integrations/StripeCustomersDialog';
import { CustomerViewPropertiesBlock } from './CustomerViewPropertiesBlock';

export const CustomerInfoViewEditSettings: React.FC = () => {
  const { getFieldState, register, setValue, resetField, trigger, getValues, watch } = useFormContext<UpdateCustomer>();
  const [showStripeCustomersDialog, setShowStripeCustomersDialog] = useState(false);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const initialTaxRate = getValues('settings.taxRate');
  watch('settings.paymentProvider');

  const { ref: _ref, ...taxRegister } = vyFormRegister(register, 'settings.taxRate', {
    valueAsNumber: true,
  });

  const handleCancelClick = () => {
    resetField('settings.taxRate');
    resetField('settings.currency');
    resetField('settings.paymentProvider');
    resetField('settings.paymentDueDays');
    resetField('settings.overage');
    resetField('settings.wallet');
  };

  const handleSaveClick = () => {
    return trigger(['settings.taxRate', 'settings.currency', 'settings.paymentProvider', 'settings.paymentDueDays', 'settings.overage', 'settings.wallet']);
  };

  return (
    <CustomerViewPropertiesBlock
      childrenContainerClassName='flex-col flex-nowrap gap-8'
      className='pb-6'
      title='Settings'
      showEditButton
      onEditChange={setIsInEditMode}
      sectionToSubmit={UPDATE_CUSTOMER_SECTIONS_TO_SUBMIT.settings}
      onCancel={handleCancelClick}
      onSave={handleSaveClick}
    >
      <div
        className='flex'
      >
        <div className='flex w-1/4 flex-col border-0 pr-6'>
          <Input
            label='Tax'
            {...taxRegister}
            defaultValue={initialTaxRate ?? 0}
            onChange={(e) => { setValue('settings.taxRate', Number(e.target.value) ?? 0); }}
            debounce={false}
            editModeOverride
            isInEditModeOverride={isInEditMode}
            editMode
            errorMessage={getFieldState('settings.taxRate')?.error?.message}
            placeholder="Tax %"
          />
        </div>

        <div
          className='flex w-1/4 flex-col border-0 pr-6'
        >
          <Controller
            name="settings.currency"
            render={({ field }) => (
              <CurrenciesSelect
                focused={isInEditMode}
                disabled={!isInEditMode}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
    
        <div
          className='flex w-1/4 flex-col border-0 pr-6'
        >
          <Controller
            name="settings.paymentDueDays"
            render={({ field }) => (
              <InvoiceDueDateSelect
                focused={isInEditMode}
                disabled={!isInEditMode}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </div>
        <div
          className='flex w-1/4 flex-col  border-0 pr-6'
        >
          <Controller
            name="settings.overage"
            render={({ field }) => (
              <>
                <Typography
                  variant='h3'
                  className='text-label mb-2 text-sm'
                >
                  Overage
                </Typography>
                <StyledTextField
                  variant='standard'
                  select
                  focused={isInEditMode}
                  disabled={!isInEditMode}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  SelectProps={{
                    SelectDisplayProps: {
                      className: `${isInEditMode ? 'py-3 pl-6' : 'pb-[0]'} duration-200 linear`,
                    },
                    IconComponent: isInEditMode ? undefined : () => null,
                    MenuProps: {
                      MenuListProps: {
                        className: 'p-0',
                      },
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  {...field}
                >
                  {values(OVERGAE_SETTINGS).map((overage) => (
                    <MenuItem
                      key={overage}
                      value={overage}
                    >
                      {getOverageSettingDescription(overage)}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </>
            )}
          />
        </div>
      </div>

      <div
        className='flex'
      >
        <div
          className='flex w-1/4 flex-col border-0 pr-6'
        >
          <Controller
            name="settings.paymentProvider"
            render={({ field }) => (
              <PaymentProviderSelect
                focused={isInEditMode}
                disabled={!isInEditMode}
                {...field}
                suffixByFieldValue={{
                  [PAYMENT_PROVIDERS.Stripe]: getPaymentProviderStripeSuffix(getValues('settings')),
                }}
                onChange={(event) => {
                  field.onChange(event);

                  if (event.target.value === PAYMENT_PROVIDERS.Stripe) {
                    setShowStripeCustomersDialog(true);
                  }
                }}
              />
            )}
          />
          {getValues('settings.paymentProvider') === PAYMENT_PROVIDERS.Stripe && isNotEmptyString(getValues('settings')?.paymentProviderMetadata?.providerExternalId) && isInEditMode && (
            <Button
              variant='text'
              color='primary'
              onClick={() => setShowStripeCustomersDialog(true)}
              sx={{ '&:hover': { backgroundColor: 'transparent' } }}
            >
              Change Stripe customer
            </Button>
          )}
          <StripeCustomerDialogForm
            open={showStripeCustomersDialog}
            onClose={() => setShowStripeCustomersDialog(false)}
            onChoose={(stripeCustomerId) => setValue('settings.paymentProviderMetadata.providerExternalId', stripeCustomerId)}
          />
        </div>

        <div className='flex w-1/4 flex-col  border-0 pr-6' >
          <Input 
            label="Wallet"
            {...register('settings.wallet')}
            isInEditModeOverride={isInEditMode}
            editModeOverride
            editMode
            placeholder='Text here'
            debounce={false}
            defaultValue={getValues('settings.wallet')}
          />
        </div>

      </div>
    </CustomerViewPropertiesBlock>
  );
};

function getPaymentProviderStripeSuffix(customerSettings: Optional<CustomerSettings>): string {
  return isNotEmptyString(customerSettings?.paymentProviderMetadata?.providerExternalId)
    ? `- ${customerSettings!.paymentProviderMetadata!.providerExternalId}`
    : '';
}